<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from "vue";
import { Chart, registerables, type ChartConfiguration, type ChartType } from "chart.js";

Chart.register(...registerables);

const props = defineProps<{
    type: ChartType;
    data: unknown;
    options?: unknown;
    height?: string;
}>();

const canvasRef = ref<HTMLCanvasElement | null>(null);
let chartInstance: Chart | null = null;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

const initChart = () => {
    if (!canvasRef.value) return;

    if (chartInstance) {
        chartInstance.destroy();
    }

    const config: ChartConfiguration = {
        type: props.type,
        data: props.data as ChartConfiguration["data"],
        options: {
            ...(isRecord(props.options) ? props.options : {}),
            maintainAspectRatio: false,
        } as ChartConfiguration["options"],
    };

    chartInstance = new Chart(canvasRef.value, config);
};

onMounted(() => {
    initChart();
});

onUnmounted(() => {
    if (chartInstance) {
        chartInstance.destroy();
    }
});

watch(() => props.data, () => {
    if (chartInstance) {
        chartInstance.data = props.data as ChartConfiguration["data"];
        chartInstance.update();
    }
}, { deep: true });

watch(() => props.options, () => {
    initChart();
}, { deep: true });
</script>

<template>
    <div :style="{ height: height || '100%', position: 'relative' }">
        <canvas ref="canvasRef"></canvas>
    </div>
</template>
